/**
 * Decision 0144 (experiment 11, direction B "peek and act"): what a ref chip opens. `RefPreview` is the read-only hover
 * card (hover or keyboard focus after 300 ms; never holds buttons, so a passing mouse never acts). `RefPeekBody` is the
 * popover's content (a bottom sheet on a phone): the key facts, and an open card in its shape, decided in place. Built
 * as a body so the ux-pass-3 right panel can host it later. `FloatingPanel` places either next to its chip.
 */
import type { RefType, RefView } from "@minga/protocol"
import { type ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { cn } from "@/lib/utils"
import type { RefDecide } from "../markdown/refs"
import { CardShapeView } from "./card-shape-view"
import { Glyph, MarkGlyph, RefIcon } from "./ref-icons"

/** The type in words, for the chip's accessible name and the preview's first row. */
export const TYPE_WORDS: Record<RefType, string> = {
  item: "Item",
  card: "Card",
  run: "Run",
  validator: "Validator run",
  output: "Output",
  goal: "Goal",
  kpi: "KPI",
  memory: "Memory",
  decision: "Decision",
}

/**
 * Decision 0159 (0144 §2): a decided card reads by its decision's ref everywhere: a `P-3` chip of a card decided as
 * `D-1` shows "D-1" (the preview still names the card). Null for open cards, cards decided before 0144 (no D-n) and
 * every other ref.
 */
export const decidedRefOf = (v: RefView): string | null =>
  v.type === "card" && v.mark === "done" && v.card?.decisionRef ? v.card.decisionRef : null

/** The ref a chip shows: the D-n of a decided card, else the ref as written. */
export const shownRef = (v: RefView, written: string): string => decidedRefOf(v) ?? written

/** "Card P-210, waits on you: Start sync PR 1 now?" (README §4 B: the chip's accessible name). */
export const chipName = (v: RefView): string => {
  const decided = decidedRefOf(v)
  const kind = decided ? TYPE_WORDS.decision : v.type === "item" ? v.variant : TYPE_WORDS[v.type]
  const status = v.status ? `, ${v.status.charAt(0).toLowerCase()}${v.status.slice(1)}` : ""
  return `${kind} ${decided ?? v.ref}${status}: ${v.title}`
}

/** The first row of a preview or popover: "Task · dictly.app · DIC-103"; a decided card "Decision D-1 · from card P-3". */
const headLine = (v: RefView): string =>
  (decidedRefOf(v)
    ? [`${TYPE_WORDS.decision} ${decidedRefOf(v)}`, `from card ${v.ref}`, v.projectName]
    : v.type === "card" || v.type === "decision"
      ? [`${TYPE_WORDS[v.type]} ${v.ref}`, v.variant, v.projectName]
      : [v.variant, v.projectName, v.ref]
  )
    .filter((x) => x !== "")
    .join(" · ")

const GUTTER = 16
const GAP = 6

/**
 * A panel fixed next to its anchor (below it, or above when there is no room), kept inside the viewport with a 16 px
 * gutter, in a portal so no scroll container clips it. It follows its anchor on scroll and resize.
 */
export function FloatingPanel({
  anchor,
  width,
  className,
  children,
  panelRef,
  ...rest
}: {
  anchor: HTMLElement | null
  width: number
  className?: string
  children: ReactNode
  panelRef?: React.RefObject<HTMLDivElement | null>
} & Omit<React.HTMLAttributes<HTMLDivElement>, "children" | "className">) {
  const own = useRef<HTMLDivElement>(null)
  const ref = panelRef ?? own
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  const place = useCallback(() => {
    if (!anchor || typeof window === "undefined") return
    const a = anchor.getBoundingClientRect()
    const h = ref.current?.offsetHeight ?? 0
    const vw = window.innerWidth
    const vh = window.innerHeight
    const w = Math.min(width, vw - 2 * GUTTER)
    const left = Math.max(GUTTER, Math.min(a.left - 6, vw - w - GUTTER))
    const below = a.bottom + GAP
    // below the chip; above it when only that fits; else as low as it fits on the screen (it scrolls inside)
    const top =
      below + h <= vh - GUTTER
        ? below
        : a.top - GAP - h > GUTTER
          ? a.top - GAP - h
          : Math.max(GUTTER, vh - h - GUTTER)
    setPos({ top, left })
  }, [anchor, width, ref])
  useLayoutEffect(() => {
    place()
  }, [place])
  useEffect(() => {
    if (typeof window === "undefined") return
    window.addEventListener("scroll", place, true)
    window.addEventListener("resize", place)
    // the content grows (a note field, the result of an answer): place it again
    const ro = typeof ResizeObserver !== "undefined" && ref.current ? new ResizeObserver(() => place()) : null
    if (ro && ref.current) ro.observe(ref.current)
    return () => {
      ro?.disconnect()
      window.removeEventListener("scroll", place, true)
      window.removeEventListener("resize", place)
    }
  }, [place, ref])
  if (typeof document === "undefined") return null
  return createPortal(
    <div
      ref={ref}
      {...rest}
      style={{
        position: "fixed",
        top: pos?.top ?? -9999,
        left: pos?.left ?? -9999,
        width: `min(${width}px, calc(100vw - ${2 * GUTTER}px))`,
      }}
      className={cn("z-50 rounded-sheet border border-rule bg-surface text-ink shadow-sheet", className)}
    >
      {children}
    </div>,
    document.body,
  )
}

/** The hover card: what the ref is (read-only). */
export function RefPreview({ view }: { view: RefView }) {
  return (
    <div className="flex flex-col gap-1.5 px-3.5 py-3 text-[13px] leading-[1.45]">
      <span className="flex items-center gap-1.5 text-[12px] text-muted">
        <RefIcon type={view.type} variant={view.variant} />
        {headLine(view)}
      </span>
      <span className="text-[15px] leading-snug font-semibold text-ink">{view.title}</span>
      <dl className="grid grid-cols-[74px_1fr] gap-x-2 gap-y-0.5 text-[12.5px] text-ink-2">
        <dt className="text-muted">Status</dt>
        <dd className="m-0 flex items-center gap-1.5">
          <MarkGlyph mark={view.mark} />
          {view.status}
        </dd>
        {view.facts.slice(0, 4).map((f) => (
          <div key={f.label} className="contents">
            <dt className="text-muted">{f.label}</dt>
            <dd className="m-0">{f.value}</dd>
          </div>
        ))}
      </dl>
      {view.card?.recommendation && view.card.open && (
        <span className="text-[12.5px] text-ink-2">
          <span className="font-semibold text-needs-deep">
            {view.card.recommendation.by ?? "Minga"}
            {view.card.recommendation.label ? ` recommends ${view.card.recommendation.label}` : ""}:
          </span>{" "}
          {view.card.recommendation.reason}
        </span>
      )}
      <span className="mt-1 text-[12px] text-muted">
        Click for {view.card?.open && view.card.inPlace ? "the answers" : "more"} ·{" "}
        <kbd className="rounded-[3px] border border-b-2 border-line-control px-1 font-mono text-[12px]">
          ⌘
        </kbd>{" "}
        click to open
      </span>
    </div>
  )
}

/** The popover's head row: what it is, and "Open" (a real link to the page). */
function PeekHead({ view, onOpen }: { view: RefView; onOpen: () => void }) {
  return (
    <div className="flex items-center justify-between gap-2 border-b border-rule px-3.5 py-2.5 text-[12px] text-muted">
      <span className="flex min-w-0 items-center gap-1.5">
        <RefIcon
          type={view.type}
          variant={view.variant}
          className={view.mark === "needs" ? "text-needs" : undefined}
        />
        <span className="truncate">
          {headLine(view)}
          {view.card?.from && view.card.from !== "you" ? ` · from ${view.card.from}` : ""}
        </span>
      </span>
      <a
        href={view.href}
        className="tap inline-flex shrink-0 items-center gap-1 text-ink underline decoration-line-control underline-offset-[3px]"
        onClick={(event) => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
          event.preventDefault()
          onOpen()
        }}
      >
        Open <Glyph name="open" size={11} />
      </a>
    </div>
  )
}

/** The popover (or sheet) content for one ref. */
export function RefPeekBody({
  view,
  decide,
  onOpen,
  autoFocus = false,
}: {
  view: RefView
  decide: RefDecide | null
  onOpen: () => void
  autoFocus?: boolean
}) {
  return (
    <div className="flex flex-col">
      <PeekHead view={view} onOpen={onOpen} />
      <div className="px-4 pt-3.5 pb-4">
        {view.card ? (
          <CardShapeView
            shape={view.card}
            decide={decide}
            layout="popover"
            openHref={view.href}
            onOpen={onOpen}
            autoFocus={autoFocus}
          />
        ) : (
          <div className="flex flex-col gap-2">
            <p className="font-serif text-[18px] leading-snug text-ink">{view.title}</p>
            <p className="flex items-center gap-1.5 text-[13px] text-ink-2">
              <MarkGlyph mark={view.mark} />
              {view.status}
            </p>
            {view.facts.length > 0 && (
              <dl className="grid grid-cols-[80px_1fr] gap-x-2 gap-y-0.5 text-[13px] text-ink-2">
                {view.facts.map((f) => (
                  <div key={f.label} className="contents">
                    <dt className="text-muted">{f.label}</dt>
                    <dd className="m-0">{f.value}</dd>
                  </div>
                ))}
              </dl>
            )}
            <a
              href={view.href}
              // biome-ignore lint/a11y/noAutofocus: the popover's first action takes focus (0136 §B keyboard)
              autoFocus={autoFocus}
              className="tap mt-1 inline-flex h-8 items-center gap-1.5 self-start rounded-control border border-line-control px-3 text-[13px] text-ink no-underline hover:bg-fill"
              onClick={(event) => {
                if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
                event.preventDefault()
                onOpen()
              }}
            >
              Open page <Glyph name="open" size={11} />
            </a>
          </div>
        )}
      </div>
    </div>
  )
}
